import { useMemo } from 'react';

import { VegaLiteSpec } from '@atom/types/conversationalAnalytics';
import {
  applyAggregateTransform,
  applyTransforms,
  formatTimeData,
  getChannelTitle,
  getDisplayFieldName,
  getFieldName,
} from '@atom/utilities/conversationalAnalyticsUtilities';

export type ConversationalAnalyticsData = {
  chartData: Record<string, unknown>[];
  xDisplayField: string;
  xField: string | null;
  yField: string | null;
  colorField: string | null;
  xTitle: string | undefined;
  yTitle: string | undefined;
};

const useConversationalAnalyticsData = (
  spec: VegaLiteSpec,
): ConversationalAnalyticsData => {
  return useMemo<ConversationalAnalyticsData>(() => {
    const { data, encoding, transform } = spec;

    let transformedData = applyTransforms(data.values, transform);

    // vega-lite supports encoding-level aggregation without an explicit transform.
    // auto-apply it when no explicit aggregate transform exists.
    const hasExplicitAggregateTransform = transform?.some(
      tr => 'aggregate' in tr,
    );

    const yChannel = encoding.y || encoding.theta;
    const xChannel = encoding.x || encoding.color;

    if (
      !hasExplicitAggregateTransform &&
      yChannel?.aggregate &&
      yChannel?.field
    ) {
      const as = `${yChannel.aggregate}_${yChannel.field}`;

      transformedData = applyAggregateTransform(transformedData, {
        aggregate: [{ op: yChannel.aggregate, field: yChannel.field, as }],
        groupby: xChannel?.field ? [xChannel.field] : [],
      });
    }

    const xField = getFieldName(encoding.x) || getFieldName(encoding.color);
    const yField =
      getDisplayFieldName(encoding.y) || getDisplayFieldName(encoding.theta);
    const colorField = getFieldName(encoding.color);

    const xTitle =
      getChannelTitle(encoding.x) || getChannelTitle(encoding.color);
    const yTitle =
      getChannelTitle(encoding.y) || getChannelTitle(encoding.theta);

    const { data: chartData, displayField: xDisplayField } = formatTimeData(
      transformedData,
      xField || '',
      encoding.x?.timeUnit,
    );

    return {
      chartData,
      xDisplayField,
      xField,
      yField,
      colorField,
      xTitle,
      yTitle,
    };
  }, [spec]);
};

export default useConversationalAnalyticsData;
